import { ArcLayer, ScatterplotLayer } from "@deck.gl/layers";
import DeckGL from "@deck.gl/react";
import { type MapPoint, toArcData, toScatterData } from "@ondetatu/shared";
import { useEffect, useMemo, useState } from "react";
import { Map as MapGL } from "react-map-gl/maplibre";
import { Scrubber } from "../../components/Scrubber.js";
import { viewsApi } from "../../lib/api/views.js";
import { useViewState } from "../../lib/view-state.js";
import "maplibre-gl/dist/maplibre-gl.css";
import "./Map.css";

// Free, no-key basemap (CARTO Positron) — attribution is baked into the style.
const BASEMAP = "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

const INITIAL = { longitude: 8, latitude: 30, zoom: 1.4, pitch: 0, bearing: 0 };

export function WorldMap() {
  const { asOf } = useViewState();
  const [points, setPoints] = useState<MapPoint[]>([]);
  const [selected, setSelected] = useState<MapPoint | null>(null);
  const [view, setView] = useState(INITIAL);

  useEffect(() => {
    viewsApi.map(asOf).then((r) => {
      if (r.success && r.data) setPoints(r.data.points);
    });
  }, [asOf]);

  const layers = useMemo(() => {
    const scatter = toScatterData(points);
    const arcs = toArcData(points);
    return [
      new ArcLayer({
        id: "arcs",
        data: arcs,
        getSourcePosition: (d) => d.source,
        getTargetPosition: (d) => d.target,
        getSourceColor: [225, 96, 63, 120],
        getTargetColor: [225, 96, 63, 220],
        getWidth: 2,
      }),
      new ScatterplotLayer({
        id: "people",
        data: scatter,
        pickable: true,
        getPosition: (d) => d.position,
        getRadius: (d) => (d.isSelf ? 9 : 7),
        radiusUnits: "pixels",
        // stale presence reads as a hollow amber ring (last known, unconfirmed)
        getFillColor: (d) =>
          d.stale ? [242, 165, 65, 60] : d.isSelf ? [14, 124, 123, 230] : [225, 96, 63, 230],
        getLineColor: (d) => (d.stale ? [242, 165, 65, 255] : [255, 255, 255, 230]),
        lineWidthMinPixels: 2,
        stroked: true,
        onClick: (info) => setSelected((info.object as MapPoint) ?? null),
      }),
    ];
  }, [points]);

  function centerOnMe() {
    const me = points.find((p) => p.isSelf && p.lat != null && p.lng != null);
    if (me?.lat != null && me.lng != null) {
      setView((v) => ({ ...v, longitude: me.lng as number, latitude: me.lat as number, zoom: 4 }));
    }
  }

  return (
    <div className="map">
      <div className="map__controls">
        <Scrubber />
        <button type="button" className="map__center" onClick={centerOnMe}>
          Center on me
        </button>
      </div>
      <div className="map__canvas">
        <DeckGL
          initialViewState={view}
          viewState={view}
          onViewStateChange={(e) => setView(e.viewState as typeof INITIAL)}
          controller
          layers={layers}
        >
          <MapGL mapStyle={BASEMAP} />
        </DeckGL>
      </div>
      {selected && (
        <div className="map__card">
          <button
            type="button"
            className="map__card-close"
            aria-label="close"
            onClick={() => setSelected(null)}
          >
            ×
          </button>
          <strong>{selected.name}</strong>
          {selected.handle && <span className="map__card-handle">@{selected.handle}</span>}
          <span className="map__card-where">
            {selected.city ? `${selected.city}, ` : ""}
            {selected.country ?? "Somewhere"}
            {selected.stale
              ? " · last seen here (unconfirmed)"
              : selected.inTrip
                ? " · travelling"
                : ""}
          </span>
        </div>
      )}
      {points.length === 0 && (
        <p className="map__empty">No one to show yet — connect with your people to fill the map.</p>
      )}
    </div>
  );
}
